import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import ReactPlayer from 'react-player'
import { useDispatch } from 'react-redux'
import { trackInitiateCheckout } from 'src/common/lib/segment'
import { prependAssetHost, prependAssetHostItem } from 'src/common/utils'
import { JoinEventRes } from 'src/services/types/joinEventV2'
import { useAppSelector } from 'src/store/hooks'
import { saveOptionSelection, updateOptionClicked } from 'src/store/joinevent'
import { addToBundle } from 'src/store/joinevent/actions'
import { itemsSelector, startingPriceSelector } from 'src/store/joinevent/selectors'
import { RadixTabs } from '../Tabs/RadixTabs'

type CurrentItem = {
    challengeAddonAsset: {
        imageUri: string
    }
    itemPrice: number | string
    itemName: string
    itemDescription: string
    challengeAddonId: number | string
    challengeAddonOptions: any[]
    challengeAddonOptionId: number | string
}

interface ProductDetailProps {
    item: JoinEventRes
    challengeId: string
}

export const ProductDetail = ({ item }: ProductDetailProps) => {
    const items = useAppSelector(itemsSelector)
    const startingPrice = useAppSelector(startingPriceSelector)
    const event = useAppSelector((state) => state.joinevent)
    const user = useAppSelector((state) => state.user.data)
    const [currentItem, setCurrentItem] = useState<CurrentItem | null>(null)
    const dispatch = useDispatch()
    const router = useRouter()

    const onOptionClicked = (optionId, displayText) => {
        dispatch(
            updateOptionClicked({
                challengeAddonId: currentItem.challengeAddonId,
                displayText,
                optionId
            })
        )
    }

    const onSaveRewardOptionsClicked = () => {
        if (currentItem) {
            dispatch(
                saveOptionSelection({
                    challengeAddonId: currentItem.challengeAddonId
                })
            )
            dispatch(addToBundle())
            trackInitiateCheckout({ productData: event, user: user ?? null })
            void router.push('/cart')
        }
    }

    useEffect(() => {
        setCurrentItem(items[0])
    }, [])

    return (
        <div className="flex flex-col h-[1572px] 2xl:max-w-screen-2xl 2xl:m-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 px-5 pt-10 mb-10 gap-x-8">
                <div className="relative flex items-start justify-center">
                    <div className="flex flex-col-reverse lg:flex-row lg:max-h-45">
                        <div className="my-4 lg:my-0 lg:mx-7 lg:my-0 flex flex-row md:overflow-y-hidden overflow-auto lg:flex-col lg:overflow-y-scroll lg:overflow-x-hidden hide-webkit-scrollbar">
                            {items.map((i, idx) => {
                                return (
                                    <div
                                        onClick={() => setCurrentItem(i)}
                                        key={idx}
                                        className={`flex m-1 bg-white max-w-[100px] xl:mr-2 xl:mb-4 xl:my-0 2xl:mx-4 border-2 border-solid rounded-md ${
                                            i.challengeAddonId ===
                                            currentItem?.challengeAddonId
                                                ? 'border-yf-green'
                                                : ''
                                        }`}
                                    >
                                        <Image
                                            objectFit="contain"
                                            className="bg-gray-300 cursor-pointer rounded block"
                                            width={'100%'}
                                            height={'100%'}
                                            src={prependAssetHostItem(
                                                i.challengeAddonAsset.imageUri
                                            )}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                        {items.length && !currentItem ? (
                            <Image
                                className="bg-gray-300 rounded-xl"
                                width={800}
                                height={800}
                                objectFit="contain"
                                src={prependAssetHostItem(
                                    items[0].challengeAddonAsset.imageUri
                                )}
                            />
                        ) : currentItem ? (
                            <Image
                                className="bg-gray-300 rounded-xl"
                                width={800}
                                height={800}
                                objectFit="contain"
                                src={prependAssetHostItem(
                                    currentItem.challengeAddonAsset.imageUri
                                )}
                            />
                        ) : (
                            <Image
                                className="bg-gray-300 rounded-xl"
                                width={800}
                                height={800}
                                objectFit="contain"
                                src={prependAssetHost('/icons/no-swag.png')}
                            />
                        )}
                    </div>
                </div>
                <div className="lg:px-5">
                    <h1 className="text-3xl font-medium mb-2">{item.summary.name}</h1>
                    <p className="text-2xl text-yf-green mb-8">
                        Starting at ${startingPrice}
                    </p>

                    {currentItem && currentItem.itemDescription ? (
                        <div className="mb-8 flex justify-between">
                            <p className="text-gray-600 text-lg w-1/2 sm:w-auto">
                                Materials:
                            </p>
                            <p className=" mx-3 text-lg w-1/2 sm:w-auto">
                                {currentItem.itemDescription}
                            </p>
                        </div>
                    ) : null}

                    {items?.length > 1 ? (
                        <div className="mb-8 box-content flex flex-col md:flex-row justify-between items-start">
                            <p className="text-gray-600 text-lg w-1/3">Variants:</p>
                            <div className="flex content-end justify-end items-end mt-2 md:mt-0  flex-wrap w-full lg:w-auto lg:max-w-full">
                                {items?.map((i, idx) => {
                                    return (
                                        <button
                                            onClick={() => setCurrentItem(i)}
                                            className={`py-4 px-2 rounded-xl text-xs w-[150px] mr-3 mb-3  ${
                                                currentItem?.challengeAddonId ===
                                                i.challengeAddonId
                                                    ? 'text-white bg-yf-purple'
                                                    : 'text-black bg-gray-300'
                                            }`}
                                            key={idx}
                                        >
                                            {i.itemName}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    ) : null}

                    {currentItem?.challengeAddonOptions?.length ? (
                        <div className="mb-8 box-content flex flex-col md:flex-row justify-between items-start">
                            <p className="text-gray-600 text-lg w-1/3">Options:</p>

                            <div className="flex content-end justify-end items-end flex-wrap w-full mt-2 md:mt-0  lg:w-auto lg:max-w-full">
                                {currentItem.challengeAddonOptions.map((i) => (
                                    <button
                                        onClick={() =>
                                            onOptionClicked(
                                                i.challengeAddonOptionId,
                                                i.displayText
                                            )
                                        }
                                        key={i.challengeAddonOptionId}
                                        className={`py-4 w-[70px] rounded-xl text-base focus:outline-none mr-3 mb-3 ${
                                            items.find(
                                                (i) =>
                                                    i.challengeAddonId ===
                                                    currentItem.challengeAddonId
                                            ).challengeAddonOptionId ===
                                            i.challengeAddonOptionId
                                                ? 'text-white bg-yf-purple'
                                                : 'text-black bg-gray-300'
                                        }`}
                                    >
                                        {i.displayText}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="mb-8 flex justify-between items-center">
                        <p className="text-gray-600 text-lg">Quantity:</p>
                        <div>
                            <button
                                disabled
                                className={`py-4 w-[70px] rounded-xl text-base focus:outline-none mr-3 text-white bg-yf-purple`}
                            >
                                1
                            </button>
                        </div>
                    </div>

                    {currentItem ? (
                        <div className="flex justify-end mr-3">
                            <button
                                onClick={() => onSaveRewardOptionsClicked()}
                                className="w-full py-4 rounded-xl max-w-[450px] bg-yf-green text-white focus:outline-none"
                            >
                                <div className="flex justify-between px-6">
                                    <p className="font-medium">
                                        ${currentItem.itemPrice}
                                    </p>
                                    <p className="font-medium">
                                        <span className="mr-1">Add To Cart</span>
                                        <i className="fal fa-arrow-right"></i>
                                    </p>
                                </div>
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="bg-white w-full h-full">
                <RadixTabs
                    tabs={[
                        {
                            label: 'About',
                            value: 'about',
                            content: (
                                <div className="mt-8">
                                    {item.summary.introVideoUrl && (
                                        <div className="max-w-2xl px-4 lg:px-8 ">
                                            <div className="aspect-w-16 aspect-h-9 w-full">
                                                <ReactPlayer
                                                    playIcon={
                                                        <i className="fas fa-play text-5xl text-white z-10"></i>
                                                    }
                                                    className="react-player"
                                                    width="100%"
                                                    height="100%"
                                                    light={
                                                        item.summary
                                                            .introVideoThumbnailUrl
                                                    }
                                                    controls
                                                    url={item.summary.introVideoUrl}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div
                                        className="p-4 lg:px-8 prose max-w-full"
                                        dangerouslySetInnerHTML={{
                                            __html: item.summary.fundraisingMessage.replace(
                                                /(<|&lt;)br\s*\/*(>|&gt;)/g,
                                                ''
                                            )
                                        }}
                                    />
                                </div>
                            )
                        }
                    ]}
                />
            </div>
            {/* <Tabs
                items={[
                    {
                        href: `/races/${encodeURIComponent(challengeId)}/${
                            currentItem.challengeAddonId
                        }`,
                        isActive: true,
                        text: 'About'
                    }
                ]}
            /> */}
        </div>
    )
}
